import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { IoMdClose } from "react-icons/io";
import InputField from "components/fields/InputField";
import { debounce } from "lodash";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "10px",
};

const defaultMapCenter = {
  lat: 20.5937,
  lng: 78.9629,
};

const MapPicker = ({
  formik,
  setShowMap,
  marker,
  setMarker,
  handleMapClose,
}) => {
  const geocoder = useRef(null);
  const [address, setAddress] = useState("");
  const [mapRef, setMapRef] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [center, setCenter] = useState(defaultMapCenter);
  const autocompleteService = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyDodu0TRK-KqYRPt5_ekzLee_CoZp_5W0U",
    libraries,
  });

  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    updateMarkerAndAddress({ lat, lng });
  }, []);

  const updateMarkerAndAddress = ({ lat, lng }) => {
    setMarker({ lat, lng });
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyDodu0TRK-KqYRPt5_ekzLee_CoZp_5W0U`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.results && data.results[0]) {
          setAddress(data.results[0].formatted_address);
        } else {
          setAddress("Address not found");
        }
      });
  };

  useEffect(() => {
    const handleSetAddress = (event) => {
      if (marker) {
        fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${marker.lat},${marker.lng}&key=AIzaSyDodu0TRK-KqYRPt5_ekzLee_CoZp_5W0U`
        )
          .then((res) => res.json())
          .then((data) => {
            if (data.results && data.results[0]) {
              setAddress(data.results[0].formatted_address);
            } else {
              setAddress("Address not found");
            }
          });
      }
    };

    handleSetAddress();
  }, [marker]);

  const handleSave = async () => {
    if (!marker) return;

    setShowMap(false);
    formik.setFieldValue("location.coordinates", [marker.lng, marker.lat]);

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${marker.lat},${marker.lng}&key=AIzaSyDodu0TRK-KqYRPt5_ekzLee_CoZp_5W0U`
      );
      const data = await res.json();
      const components = data?.results?.[0]?.address_components;

      if (components) {
        const { address, city, state, country, zipcode } =
          extractAddressComponents(components);

        formik.setFieldValue("address", address);
        formik.setFieldValue("city", city);
        formik.setFieldValue("state", state);
        formik.setFieldValue("country", country);
        formik.setFieldValue("zipcode", zipcode);
      }
    } catch (error) {
      console.error("Failed to get address from coordinates:", error);
    }
  };

  const extractAddressComponents = (components) => {
    const get = (type) =>
      components.find((comp) => comp.types.includes(type))?.long_name || "";

    const parts = [
      get("premise"),
      get("route"),
      get("street_address"),
      get("plus_code"),
      get("political"),
    ].filter((p) => p);

    const fullAddress = parts.join(", ");

    return {
      address: fullAddress,
      city:
        get("locality") ||
        get("sublocality") ||
        get("administrative_area_level_2"),
      state: get("administrative_area_level_1"),
      country: get("country"),
      zipcode: get("postal_code"),
    };
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (!value) {
      setSuggestions([]);
      return;
    }

    debouncedFetchSuggestions(value);
  };

  const handleSelectSuggestion = (placeId) => {
    if (!geocoder.current) {
      geocoder.current = new window.google.maps.Geocoder();
    }

    geocoder.current.geocode({ placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        setCenter({ lat, lng });
        updateMarkerAndAddress({ lat, lng });
        setSuggestions([]);
        setSearchText(results[0].formatted_address);
      }
    });
  };

  const debouncedFetchSuggestions = debounce((value) => {
    if (!autocompleteService.current) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: value,
        types: ["geocode"],
      },
      (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions.slice(0, 5));
        } else {
          setSuggestions([]);
        }
      }
    );
  }, 300);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="relative mx-3 max-h-[90vh] w-[700px] overflow-auto rounded-xl bg-navy-800 px-6 pb-6 shadow-lg">
        <div className="relative flex justify-center py-4">
          <p className="text-2xl font-semibold text-white">Choose Location</p>
          <IoMdClose
            className="absolute right-0 cursor-pointer text-2xl text-white hover:text-orangePrimary"
            onClick={handleMapClose}
          />
        </div>

        <div className="relative mb-4">
          <InputField
            type="text"
            placeholder="Search Location..."
            // className="block w-full bg-navy-900 text-sm font-medium text-white outline-none placeholder:!text-white sm:w-fit"
            // onChange={handleSearch}
            value={searchText}
            onChange={handleSearchChange}
          />
          {suggestions.length > 0 && (
            <ul className="absolute left-0 right-0 z-10 mt-1 max-h-60 overflow-y-auto rounded-lg border border-navy-900 bg-navy-900 shadow-lg">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  className="cursor-pointer px-4 py-2 hover:bg-navy-800"
                  onClick={() => handleSelectSuggestion(suggestion.place_id)}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
        </div>

        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={5}
          center={center}
          onClick={onMapClick}
          onLoad={(map) => setMapRef(map)}
        >
          {marker && <Marker position={marker} />}
        </GoogleMap>

        {marker && (
          <div className="mt-3 flex flex-col items-start justify-between sm:flex-row">
            <div className="text-white">
              <strong>Latitude:</strong> {marker.lat} <br />
              <strong>Longitude:</strong> {marker.lng} <br />
              <strong>Address:</strong> {address}
            </div>
            <div className="flex w-full justify-end">
              <button
                onClick={handleSave}
                type="button"
                className="mt-3 flex h-10 w-fit cursor-pointer items-center whitespace-nowrap rounded-full bg-orangePrimary px-4 text-white duration-150 hover:bg-orangePrimary/80 sm:mt-0"
              >
                Save Location
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapPicker;
